import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Entry } from 'contentful';
import {
  NavigationNode,
  NavigationQuickCall,
} from 'haba-contentful-components/lib/interfaces/navigation';
import { AuthService, WindowRef } from 'haba-core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { closeHeaderNavigation } from './helper';
import Timeout = NodeJS.Timeout;

@Directive()
export abstract class NavigationComponent implements OnInit, OnDestroy {
  public isBrowser: boolean;
  public window: Window;
  public navigationNodes: Entry<NavigationNode>[];
  public quickLinks: Entry<NavigationQuickCall>[];
  public isLoggedIn = false;
  public delayTime = 200;
  public isUserLoggedInOutput$ = new Subject<boolean>();
  public currentRoute: string;

  protected unsubscribe$ = new Subject<void>();
  private timeout: Timeout;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: string,
    private authService: AuthService,
    protected winRef: WindowRef,
    protected elemRef: ElementRef,
    protected router: Router
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.window = this.document.defaultView;
  }

  ngOnInit(): void {
    this.initUserCheck();
    this.currentRoute = this.router.url;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  initUserCheck() {
    this.authService
      .isUserLoggedIn()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((loggedIn) => {
        this.isLoggedIn = loggedIn;
        this.isUserLoggedInOutput$.next(loggedIn);
      });
  }

  isTouchDevice() {
    if (this.isBrowser) {
      return 'ontouchstart' in window || this.winRef.nativeWindow?.navigator?.maxTouchPoints > 0;
    }
  }

  toggleNavigation(navigationItem: HTMLElement) {
    const targetActiveElement = navigationItem.querySelector('.main-navigation-link-lvl-1');
    if (targetActiveElement?.classList.contains('active')) {
      return;
    }

    this.closeNavigation();
    if (!this.isTouchDevice()) {
      if (this.timeout != null) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.openNavigation(navigationItem);
      }, this.delayTime);
    } else {
      this.openNavigation(navigationItem);
    }
  }

  closeNavigation() {
    clearTimeout(this.timeout);
    const element = this.elemRef.nativeElement as HTMLElement;
    element.querySelectorAll('.active').forEach((link) => {
      link?.classList.remove('active');
      closeHeaderNavigation(this.document);
    });
  }

  closeNavigationOutsideClick() {
    clearTimeout(this.timeout);
    this.document.addEventListener('click', (el: MouseEvent) => {
      const target = el.target as HTMLElement;
      if (!target.classList.contains('navigation') && !target.closest('.navigation')) {
        this.closeNavigation();
      }
    });
  }

  openNavigation(navigationItem: HTMLElement) {
    const targetActiveElement = navigationItem.querySelector('.main-navigation-link-lvl-1');
    targetActiveElement?.classList?.add('active');
  }
}
